/**
 * useUpdateRole Hook
 *
 * React Query mutation hook for updating roles.
 * Handles optimistic updates, cache invalidation, and error handling.
 *
 * @module domains/personnel/presentation/hooks/useUpdateRole
 */

import { useMutation, useQueryClient, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@parrot-software/design-system';
import { getRoleUseCases } from '@personnel/composition';
import type { Role } from '@personnel/domain/entities/Role';
import type { RoleInputDto } from '@personnel/application/dtos';
import { ROLES_QUERY_KEYS } from './useRoleList';
import { useApiErrorHandler } from './useApiErrorHandler';
import { attachFormFieldError } from '@personnel/application/errors';

/**
 * Input for updating a role
 */
export interface UpdateRoleInput {
  /** Role UUID to update */
  uuid: string;
  /** Role input data */
  data: RoleInputDto;
}

/**
 * Hook options for configuring update behavior
 */
export interface UseUpdateRoleOptions {
  /** Callback executed on successful update */
  onSuccess?: (role: Role) => void;
  /** Callback executed on error */
  onError?: (error: Error) => void;
  /** Whether to show success toast (default: true) */
  showSuccessToast?: boolean;
}

/**
 * Hook result with mutation function and states
 */
export interface UseUpdateRoleResult {
  /** Mutation function to update a role */
  updateRole: (input: UpdateRoleInput) => Promise<Role>;
  /** Whether the update is in progress */
  isLoading: boolean;
  /** Whether the update was successful */
  isSuccess: boolean;
  /** Whether the update failed */
  isError: boolean;
  /** Error object if update failed */
  error: Error | null;
  /** Reset mutation state */
  reset: () => void;
}

/**
 * React Query mutation hook for updating a role
 *
 * Features:
 * - Automatic cache invalidation
 * - Success/error toast notifications
 * - Loading and error states
 * - TypeScript type safety
 *
 * @param options - Hook configuration options
 * @returns Mutation object with updateRole function and states
 *
 * @example
 * const { updateRole, isLoading } = useUpdateRole({
 *   onSuccess: (role) => console.log('Updated:', role),
 *   showSuccessToast: true
 * });
 *
 * await updateRole({
 *   uuid: 'role-123',
 *   data: {
 *     name: 'Senior Manager',
 *     permissions: ['org-menu', 'org-personnel']
 *   }
 * });
 */
export const useUpdateRole = (options: UseUpdateRoleOptions = {}): UseUpdateRoleResult => {
  const { onSuccess, onError, showSuccessToast = true } = options;

  const queryClient = useQueryClient();
  const { handleError } = useApiErrorHandler();
  const { t: tRoles } = useTranslation('roles');

  const mutation: UseMutationResult<Role, Error, UpdateRoleInput> = useMutation({
    mutationFn: async (input: UpdateRoleInput) => {
      const { uuid, data } = input;
      return getRoleUseCases().updateRole.execute({ uuid, data });
    },
    onSuccess: (updatedRole, variables) => {
      // ✅ Invalidate roles list to refetch with updated data
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

      // ✅ Invalidate specific role detail cache
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.detail(variables.uuid));

      // ✅ Show success toast
      if (showSuccessToast) {
        toast.success(tRoles('notifications.updateSuccess'));
      }

      // ✅ Execute custom callback
      if (onSuccess) {
        onSuccess(updatedRole);
      }
    },
    onError: (error: Error) => {
      // Attach form field metadata if applicable (e.g., "role already exists" → name field)
      attachFormFieldError(error);

      // Show error toast with context (will be skipped for form-handled errors)
      handleError(error, { actionContext: 'update' });

      // ✅ Execute custom callback
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    updateRole: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error || null,
    reset: mutation.reset,
  };
};
